import React, { useEffect, useState, useCallback } from "react";
import useAuthFetch from "../hooks/useAuthFetch";

const selectClass =
  "w-full rounded-xl h-10 px-3 ring-1 ring-purple outline-none text-sm bg-white";

const ProvinciaCantonDistritoSelector = ({ onSelect, initialValues, disabled }) => {
  const authFetch = useAuthFetch();

  const [provincias, setProvincias] = useState([]);
  const [cantones, setCantones] = useState([]);
  const [distritos, setDistritos] = useState([]);

  const [selectedProvincia, setSelectedProvincia] = useState("");
  const [selectedCanton, setSelectedCanton] = useState("");
  const [selectedDistrito, setSelectedDistrito] = useState("");

  const [loadingProvincias, setLoadingProvincias] = useState(false);
  const [loadingCantones, setLoadingCantones] = useState(false);
  const [loadingDistritos, setLoadingDistritos] = useState(false);

  // Load provincias on mount
  useEffect(() => {
    const fetchProvincias = async () => {
      setLoadingProvincias(true);
      try {
        const res = await authFetch("provincias", null, "GET");
        if (res && res.ok) {
          const data = await res.json();
          setProvincias(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching provincias:", err);
      }
      setLoadingProvincias(false);
    };
    fetchProvincias();
  }, [authFetch]);

  // Load cantones when provincia changes
  const fetchCantones = useCallback(
    async (idProvincia) => {
      if (!idProvincia) {
        setCantones([]);
        return;
      }
      setLoadingCantones(true);
      try {
        const filter = JSON.stringify({ where: { idProvincia: Number(idProvincia) } });
        const res = await authFetch(`cantones?filter=${encodeURIComponent(filter)}`, null, "GET");
        if (res && res.ok) {
          const data = await res.json();
          setCantones(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching cantones:", err);
      }
      setLoadingCantones(false);
    },
    [authFetch]
  );

  // Load distritos when canton changes
  const fetchDistritos = useCallback(
    async (idProvincia, idCanton) => {
      if (!idProvincia || !idCanton) {
        setDistritos([]);
        return;
      }
      setLoadingDistritos(true);
      try {
        const filter = JSON.stringify({
          where: { idProvincia: Number(idProvincia), idCanton: Number(idCanton) },
        });
        const res = await authFetch(`distritos?filter=${encodeURIComponent(filter)}`, null, "GET");
        if (res && res.ok) {
          const data = await res.json();
          setDistritos(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        console.error("Error fetching distritos:", err);
      }
      setLoadingDistritos(false);
    },
    [authFetch]
  );

  // Handle initialValues on mount
  useEffect(() => {
    if (initialValues) {
      if (initialValues.idProvincia) {
        setSelectedProvincia(String(initialValues.idProvincia));
        fetchCantones(initialValues.idProvincia);
      }
      if (initialValues.idCanton) {
        setSelectedCanton(String(initialValues.idCanton));
      }
      if (initialValues.idDistrito) {
        setSelectedDistrito(String(initialValues.idDistrito));
      }
      if (initialValues.idProvincia && initialValues.idCanton) {
        fetchDistritos(initialValues.idProvincia, initialValues.idCanton);
      }
    }
  }, []); // Run once on mount

  // When provincia changes
  const handleProvinciaChange = (e) => {
    const value = e.target.value;
    setSelectedProvincia(value);
    setSelectedCanton("");
    setSelectedDistrito("");
    setCantones([]);
    setDistritos([]);
    if (value) {
      fetchCantones(value);
    }
  };

  // When canton changes
  const handleCantonChange = (e) => {
    const value = e.target.value;
    setSelectedCanton(value);
    setSelectedDistrito("");
    setDistritos([]);
    if (value && selectedProvincia) {
      fetchDistritos(selectedProvincia, value);
    }
  };

  // When distrito changes
  const handleDistritoChange = (e) => {
    const value = e.target.value;
    setSelectedDistrito(value);

    if (value && selectedProvincia && selectedCanton && onSelect) {
      const provincia = provincias.find((p) => String(p.idProvincia) === selectedProvincia);
      const canton = cantones.find((c) => String(c.idCanton) === selectedCanton);
      const distrito = distritos.find((d) => String(d.idDistrito) === value);

      onSelect({
        idProvincia: Number(selectedProvincia),
        idCanton: Number(selectedCanton),
        idDistrito: Number(value),
        nombreProvincia: provincia?.nombre || "",
        nombreCanton: canton?.nombre || "",
        nombreDistrito: distrito?.nombre || "",
      });
    }
  };

  return (
    <div className="flex flex-wrap gap-2 items-end">
      {/* Provincia */}
      <div className="min-w-[140px] flex-1">
        <label className="block text-xs mb-1">Provincia</label>
        {loadingProvincias ? (
          <div className={`${selectClass} flex items-center text-gray-400`}>Cargando...</div>
        ) : (
          <select
            className={selectClass}
            value={selectedProvincia}
            onChange={handleProvinciaChange}
            disabled={disabled}
          >
            <option value="">Seleccione...</option>
            {provincias.map((p) => (
              <option key={p.idProvincia} value={p.idProvincia}>
                {p.nombre}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Cantón */}
      <div className="min-w-[140px] flex-1">
        <label className="block text-xs mb-1">Cantón</label>
        {loadingCantones ? (
          <div className={`${selectClass} flex items-center text-gray-400`}>Cargando...</div>
        ) : (
          <select
            className={selectClass}
            value={selectedCanton}
            onChange={handleCantonChange}
            disabled={disabled || !selectedProvincia}
          >
            <option value="">Seleccione...</option>
            {cantones.map((c) => (
              <option key={`${c.idProvincia}-${c.idCanton}`} value={c.idCanton}>
                {c.nombre}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Distrito */}
      <div className="min-w-[140px] flex-1">
        <label className="block text-xs mb-1">Distrito</label>
        {loadingDistritos ? (
          <div className={`${selectClass} flex items-center text-gray-400`}>Cargando...</div>
        ) : (
          <select
            className={selectClass}
            value={selectedDistrito}
            onChange={handleDistritoChange}
            disabled={disabled || !selectedCanton}
          >
            <option value="">Seleccione...</option>
            {distritos.map((d) => (
              <option key={`${d.idProvincia}-${d.idCanton}-${d.idDistrito}`} value={d.idDistrito}>
                {d.nombre}
              </option>
            ))}
          </select>
        )}
      </div>
    </div>
  );
};

export default ProvinciaCantonDistritoSelector;
